import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import Select from "react-select"

interface CommonProps<T> {
  selectedOption?: T | null
  options: T[]
  placeholder?: string
  isLoading?: boolean,
  isDisabled?: boolean,
  getOptionLabel: (option: T) => string
}

type ConditionalProps<T> =
{
  isClearable: true;
  onChange: (option: T | null) => void
}
| {
  isClearable?: false;
  onChange: (option: T) => void
};

type Props<T> = ConditionalProps<T> & CommonProps<T>

/**
 * A component designed to handle React Select styling and offer an easier to use interface for it
 */
export function SimpleSelect<T>({
  selectedOption,
  options,
  placeholder,
  isClearable,
  isLoading,
  isDisabled,
  getOptionLabel,
  onChange,
}: Props<T>) {
  const { t } = useTranslation()
  const selectOptions = useMemo(() => options.map(option => ({
    value: option,
    label: getOptionLabel(option)
  })), [options, getOptionLabel])

  return <Select
    value={selectOptions.find(option => option.value === selectedOption) ?? null}
    options={selectOptions}
    onChange={option => onChange((option as {value: T, label: string})?.value)}
    placeholder={placeholder || t("Select...")}
    isClearable={isClearable}
    isLoading={isLoading}
    isDisabled={isDisabled}
    theme={theme => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary: "#454545",
        primary25: "#ededed",
      },
    })}
  />
}